import type { AnalyticsEvent } from './types';

/** Server-side EventBatch accepts at most 1000 events; stay well under. */
export const MAX_BATCH_SIZE = 500;

/**
 * Browsers cap the keepalive bodies (beacons and keepalive fetches) a page
 * has in flight at 64 KiB in total, so one page hide can deliver about one
 * body that size. Measured in UTF-8 bytes.
 */
const MAX_KEEPALIVE_BYTES = 60_000;
const utf8Length = (s: string) => new TextEncoder().encode(s).length;
/** Honour a server's Retry-After, but never wait longer than this. */
const MAX_RETRY_AFTER_MS = 5 * 60_000;

export type SendResult = {
  status: 'sent' | 'retry' | 'drop';
  /** From a Retry-After header on 429/503, when the server sent one. */
  retryAfterMs?: number;
};

/** HTTP delivery to the ingestion API: fetch for normal flushes, sendBeacon on page hide. */
export class Transport {
  private batchUrl: string;

  constructor(
    host: string,
    private writeKey: string,
  ) {
    this.batchUrl = `${host.replace(/\/+$/, '')}/api/v1/ingest/batch`;
  }

  /**
   * POST a batch. 'retry' means the events should stay queued (network
   * error, 429, 5xx); 'drop' means retrying cannot succeed (other 4xx —
   * the payload itself is rejected).
   */
  async sendBatch(events: AnalyticsEvent[]): Promise<SendResult> {
    try {
      const body = JSON.stringify({ events });
      const response = await fetch(this.batchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Narrato-Write-Key': this.writeKey,
        },
        body,
        // Lets a send that's in flight at page unload finish (the beacon
        // skips in-flight events). Browsers cap keepalive bodies at 64 KB.
        keepalive: utf8Length(body) < MAX_KEEPALIVE_BYTES,
      });
      if (response.ok) return { status: 'sent' };
      if (response.status === 429 || response.status >= 500) {
        const seconds = Number(response.headers.get('Retry-After'));
        return seconds > 0
          ? { status: 'retry', retryAfterMs: Math.min(seconds * 1000, MAX_RETRY_AFTER_MS) }
          : { status: 'retry' };
      }
      return { status: 'drop' };
    } catch {
      return { status: 'retry' };
    }
  }

  /**
   * Fire-and-forget delivery that survives page unload. sendBeacon cannot
   * set headers, so the write key rides in the query string. The body is
   * JSON sent as text/plain: a CORS-safelisted type, so the cross-origin
   * beacon needs no preflight (application/json is either refused or sent
   * as a credentialed request no wildcard CORS answer satisfies). Sends
   * the oldest events that fit one keepalive body and returns the rest
   * (plus those, if the browser refused) for the caller to keep queued.
   */
  beacon(events: AnalyticsEvent[]): AnalyticsEvent[] {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return events;
    }
    const parts: string[] = [];
    let bytes = '{"events":[]}'.length;
    for (const event of events) {
      const part = JSON.stringify(event);
      const size = utf8Length(part) + 1;
      if (parts.length > 0 && bytes + size > MAX_KEEPALIVE_BYTES) break;
      parts.push(part);
      bytes += size;
    }
    const url = `${this.batchUrl}?writeKey=${encodeURIComponent(this.writeKey)}`;
    const blob = new Blob([`{"events":[${parts.join(',')}]}`], {
      type: 'text/plain;charset=UTF-8',
    });
    let accepted = false;
    try {
      accepted = navigator.sendBeacon(url, blob);
    } catch {
      // refused
    }
    return accepted ? events.slice(parts.length) : events;
  }
}
